import {
  CurrencyCode,
  Precision,
  ParsableNumber,
  CountryCode,
} from './typings';
import { COUNTRY_CODE, CURRENCY_ID } from './constants';
import { CurrencyConfig, DEFAULT_SEPARATOR_PATTERN } from './config';
/**
 * @module utils
 */

/**
 * If input is a finite number then do something and return result
 * @private
 * @since 2.0.0
 * @param {*} input
 * @param {Function} func  Must be (number) => any.
 * @param {?*} invalidReturn
 * @return {*}
 */
export function ifNumberValidThen<I, FR, R = never>(
  input: I,
  func: (input: number) => FR,
  invalidReturn?: R
): number | FR | R | I {
  const num = parseFloat(String(input));
  return isFinite(num)
    ? typeof func === 'function'
      ? func(num)
      : num
    : typeof invalidReturn !== 'undefined'
    ? invalidReturn
    : input;
}

/**
 * Use separator to separate a string with regex match
 * @private
 * @since 2.0.0
 * @param {string} str
 * @param {string} separator
 * @return {string}
 */
export function getReadableInteger(
  str: string,
  options?: Pick<CurrencyConfig, 'separatorPattern' | 'separator'>
): string {
  const { separator = ',', separatorPattern = DEFAULT_SEPARATOR_PATTERN } =
    options || {};
  if (typeof str !== 'string') {
    return '';
  }
  if (typeof separator !== 'string') {
    return str;
  }
  const separatedArr = [];

  let i = str.length;
  let j = 0;
  while (i > 0) {
    const separateCount = separatorPattern[j % separatorPattern.length];
    const strGroup = str.slice(Math.max(i - separateCount, 0), i);
    separatedArr.unshift(strGroup);
    i -= separateCount;
    j++;
  }
  return separatedArr.join(separator);
}

/**
 * Convert number to Float object
 * @private
 * @param {number} number
 * @return {Float}
 */
function numToFloat(number: number) {
  const [mant, exp] = Number(number)
    .toExponential()
    .split('e')
    .map((str) => +str);
  return {
    mant,
    exp,
  };
}

/**
 * Generate zeros
 * @private
 * @param {number} count
 * @return {string}
 */
export function generateZeros(count: number) {
  let zeros = '';
  for (let i = 0; i < count; i++) {
    zeros += '0';
  }
  return zeros;
}

/**
 * Custom toFixed function to handle the JS toFixed precision rounding issue
 * Ref: https://github.com/terrymorse58/round-tofixed
 * @private
 * @param {number} number
 * @param {number} precision
 * @return {string}
 */
function toFixed(number: number, precision: number) {
  const floatVal = numToFloat(number);
  const newFloatVal = numToFloat(
    Math.round(
      +(String(floatVal.mant) + 'e' + String(floatVal.exp + precision))
    )
  );
  const fixedNumber = String(
    +(String(newFloatVal.mant) + 'e' + String(newFloatVal.exp - precision))
  );

  if (precision === 0 || fixedNumber.includes('e')) {
    return fixedNumber;
  }

  const dotPos = fixedNumber.indexOf('.');
  if (dotPos === -1) {
    return fixedNumber + '.' + generateZeros(precision);
  }
  return (
    fixedNumber + generateZeros(precision - (fixedNumber.length - dotPos) + 1)
  );
}

/**
 * Round a decimal number
 * @private
 * @param {*} input
 * @param {?number} precision
 * @return {string}
 */
export function roundDecimal(
  input: ParsableNumber,
  precision?: Precision
): string {
  return ifNumberValidThen(
    input,
    (number) =>
      typeof precision === 'number' && precision >= 0 && precision < 100
        ? toFixed(number, precision)
        : number.toString(),
    ''
  ) as string;
}

/**
 * Separate parts of a number
 * @param {string} input
 * @param {string} decimalPoint='.'
 * @return {{ symbol: string, integer: string, decimal: void | string, decimalPoint: string }}
 */
export function getNumberParts(
  input: string,
  decimalPoint = '.'
): {
  symbol: string;
  integer: string;
  decimal: void | string;
  decimalPoint: string;
} {
  if (typeof input === 'string') {
    const integerStartPos = input.search(/^-|\+/) + 1;
    const decimalPos = input.lastIndexOf(decimalPoint);
    return {
      symbol: input.slice(0, integerStartPos),
      integer: input.slice(
        integerStartPos,
        decimalPos < 0 ? void 0 : decimalPos
      ),
      decimal: decimalPos < 0 ? void 0 : input.slice(decimalPos + 1),
      decimalPoint,
    };
  }
  return {
    symbol: '',
    integer: '',
    decimal: '',
    decimalPoint,
  };
}

/**
 * @private
 * @param input {*}
 * @return {boolean}
 */
export function isCurrencyCode(input: unknown): input is CurrencyCode {
  return typeof input === 'string' && input.toUpperCase() in CURRENCY_ID;
}

/**
 * @private
 * @param input {*}
 * @return {boolean}
 */
export function isCountryCode(input: unknown): input is CountryCode {
  return typeof input === 'string' && input.toUpperCase() in COUNTRY_CODE;
}
